

import styled from 'styled-components';

import { colors, fonts } from '../../shared/tokens';
import { Section, Inner } from '../landingShared';

const A = '/images/redesign/landing';

const Platform = () => (
  <Section $bg="#0d1420">
    <Inner>
      <Head>
        <Heading>
          All the Tools You Need
          <br />
          in <em>One Unified</em> Platform
        </Heading>
        <HeadRight>
          <HeadDesc>
            Every feature engineered for industrial-grade reliability — live
            sensor data, automated heating programs, critical alerts, and deep
            analytics. Security-first by design.
          </HeadDesc>
          <ViewBtn type="button">View Platform →</ViewBtn>
        </HeadRight>
      </Head>

      <Grid>
        <Card>
          <FeatureHeader
            icon="feat-monitoring.svg"
            title="Monitoring"
            desc="Real-time visibility of temperature, sensors, and system health across all sites and zones."
          />
          <Widget>
            <WidgetLabel>Live Temp · Zone BET EAST · 24H</WidgetLabel>
            <BigReadout>
              <Big>356</Big>
              <Unit>°C</Unit>
              <Trend>+2.4%</Trend>
            </BigReadout>
            <Sparkline src={`${A}/feat-sparkline.svg`} alt="" />
            <WidgetFoot>
              <span>245°C avg</span>
              <em>18 Active Sensors ●</em>
            </WidgetFoot>
          </Widget>
        </Card>

        <Card>
          <FeatureHeader
            icon="feat-safety.svg"
            iconTone="amber"
            title="Safety & Alerts"
            desc="Instant alerts and intelligent detection to prevent issues before they escalate. Never miss a critical event."
          />
          <Widget>
            <WidgetLabel>Recent Alerts</WidgetLabel>
            <AlertList>
              <AlertItem $dot={colors.red}>
                <span>High Temperature · Heater Zone 3</span>
                <Tag $c={colors.red}>CRITICAL</Tag>
              </AlertItem>
              <AlertItem $dot="#f0a500">
                <span>Sensor Offline · Weather Node A1</span>
                <Tag $c="#f0a500">WARNING</Tag>
              </AlertItem>
              <AlertItem $dot="#f0a500">
                <span>Wind Factor High · North Yard</span>
                <Tag $c="#f0a500">WARNING</Tag>
              </AlertItem>
            </AlertList>
          </Widget>
        </Card>

        <Card>
          <FeatureHeader
            icon="feat-operations.svg"
            title="Operations Control"
            desc="Control heating systems and environmental parameters remotely. Activate programs across all zones."
          />
          <Widget>
            <OpsHead>
              <WidgetLabel>Active Programs</WidgetLabel>
              <OpsCount>3 ACTIVE</OpsCount>
            </OpsHead>
            <OpsList>
              {[
                ['Instant Heat Program', false],
                ['Snow Sensor Program', true],
                ['Heating Schedule', true],
                ['Wind Factor Program', true],
              ].map(([name, on], i, arr) => (
                <OpsRow key={name} $last={i === arr.length - 1}>
                  <span>{name}</span>
                  <Toggle $on={on}>
                    <Knob $on={on} />
                  </Toggle>
                </OpsRow>
              ))}
            </OpsList>
          </Widget>
        </Card>

        <Card>
          <FeatureHeader
            icon="feat-reporting.svg"
            title="Reporting & Insights"
            desc="Actionable data reports to drive performance, compliance, and energy optimization."
          />
          <Widget>
            <WidgetLabel>Energy Consumption · 7D</WidgetLabel>
            <BigReadout>
              <Big>4.72</Big>
              <UnitBold>MWh</UnitBold>
            </BigReadout>
            <TrendLine>▲ +3.4% vs last 7 days</TrendLine>
            <Bars>
              {[24, 31, 20, 35, 26, 33, 40].map((h, i, arr) => (
                <Bar key={i} style={{ height: `${h}px` }} $active={i === arr.length - 1} />
              ))}
            </Bars>
          </Widget>
        </Card>
      </Grid>
    </Inner>
  </Section>
);

export default Platform;



const FeatureHeader = ({ icon, iconTone, title, desc }) => (
  <>
    <IconBox $tone={iconTone}>
      <img src={`${A}/${icon}`} alt="" width={22} height={22} />
    </IconBox>
    <CardTitle>{title}</CardTitle>
    <CardDesc>{desc}</CardDesc>
  </>
);



const Head = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 40px;

  @media (max-width: 900px) {
    flex-direction: column;
    gap: 20px;
  }
`;

const Heading = styled.h2`
  font-family: ${fonts.condensed};
  font-weight: 900;
  font-size: 52px;
  line-height: 52px;
  text-transform: uppercase;
  color: ${colors.ink};
  max-width: 410px;
  em {
    font-style: normal;
    color: ${colors.cyan};
  }
`;

const HeadRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 20px;
  max-width: 410px;

  @media (max-width: 900px) {
    align-items: flex-start;
  }
`;

const HeadDesc = styled.p`
  font-family: ${fonts.body};
  font-weight: 300;
  font-size: 15px;
  line-height: 25.5px;
  color: ${colors.muted};
  text-align: right;

  @media (max-width: 900px) {
    text-align: left;
  }
`;

const ViewBtn = styled.button`
  padding: 11.75px 26.75px;
  border: 0.75px solid #1e8a93;
  border-radius: 3px;
  font-family: ${fonts.condensed};
  font-weight: 700;
  font-size: 13px;
  letter-spacing: 1.3px;
  text-transform: uppercase;
  color: ${colors.cyan};
  cursor: pointer;
  transition: background 0.15s ease;
  &:hover { background: rgba(56, 200, 212, 0.08); }
`;



const Grid = styled.div`
  margin-top: 64px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  position: relative;
  padding: 40.75px;
  background: #111b2e;
  border: 0.75px solid ${colors.hairline};
  display: flex;
  flex-direction: column;
  transition: transform 0.3s ease, background 0.3s ease, border-color 0.3s ease;


  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: ${colors.cyan};
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.4s cubic-bezier(0.22, 1, 0.36, 1);
  }

  &:hover {
    transform: translateY(-4px);
    background: #131f33;
    border-color: ${colors.cyanBorderSoft};
  }
  &:hover::before {
    transform: scaleX(1);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    &:hover {
      transform: none;
    }
    &:hover::before {
      transform: scaleX(0);
    }
  }

  @media (max-width: 460px) {
    padding: 28px;
  }
`;

const IconBox = styled.div`
  width: 46px;
  height: 46px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $tone }) =>
    $tone === 'amber' ? 'rgba(240,165,0,0.08)' : 'rgba(56,200,212,0.08)'};
  border: 0.75px solid ${({ $tone }) =>
    $tone === 'amber' ? 'rgba(240,165,0,0.18)' : 'rgba(56,200,212,0.18)'};
`;

const CardTitle = styled.h3`
  margin-top: 19.75px;
  font-family: ${fonts.condensed};
  font-weight: 800;
  font-size: 24px;
  line-height: 36px;
  text-transform: uppercase;
  color: ${colors.ink};
`;

const CardDesc = styled.p`
  margin-top: 8px;
  max-width: 367px;
  font-family: ${fonts.body};
  font-weight: 300;
  font-size: 14px;
  line-height: 22.4px;
  color: ${colors.muted};
`;



const Widget = styled.div`
  margin-top: 20px;
  padding: 14.75px;
  border-radius: 6px;
  background: #0d1420;
  border: 0.75px solid ${colors.hairline};
`;

const WidgetLabel = styled.p`
  font-family: ${fonts.mono};
  font-size: 9px;
  line-height: 13.5px;
  letter-spacing: 0.72px;
  text-transform: uppercase;
  color: ${colors.muted2};
`;

const BigReadout = styled.div`
  margin-top: 10px;
  display: flex;
  align-items: baseline;
  gap: 6px;
`;

const Big = styled.span`
  font-family: ${fonts.condensed};
  font-weight: 800;
  font-size: 40px;
  line-height: 40px;
  color: ${colors.ink};
`;

const Unit = styled.span`
  font-family: ${fonts.body};
  font-size: 16px;
  color: ${colors.muted};
`;

const UnitBold = styled.span`
  font-family: ${fonts.body};
  font-weight: 800;
  font-size: 14px;
  color: ${colors.muted};
`;

const Trend = styled.span`
  margin-left: auto;
  font-family: ${fonts.mono};
  font-size: 10px;
  color: ${colors.cyan};
`;

const Sparkline = styled.img`
  margin-top: 4px;
  width: 100%;
  height: 40px;
`;

const WidgetFoot = styled.div`
  margin-top: 6px;
  display: flex;
  justify-content: space-between;
  font-family: ${fonts.mono};
  font-size: 9px;
  span { color: ${colors.muted}; }
  em { font-style: normal; color: ${colors.cyan}; }
`;

const TrendLine = styled.p`
  margin-top: 2px;
  font-family: ${fonts.body};
  font-size: 11px;
  line-height: 16.5px;
  color: ${colors.cyan};
`;


const AlertList = styled.div`
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const AlertItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 8px;
  border-radius: 3px;
  background: #080c14;

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 3px;
    background: ${({ $dot }) => $dot};
    flex-shrink: 0;
  }

  span {
    flex: 1;
    min-width: 0;
    font-family: ${fonts.body};
    font-size: 10px;
    line-height: 15px;
    color: ${colors.textSoft};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const Tag = styled.span`
  flex-shrink: 0;
  padding: 2px 6px;
  border-radius: 2px;
  background: ${({ $c }) =>
    $c === colors.red ? 'rgba(224,52,52,0.15)' : 'rgba(240,165,0,0.15)'};
  font-family: ${fonts.mono} !important;
  font-weight: 700;
  font-size: 8px !important;
  color: ${({ $c }) => $c} !important;
`;


const OpsHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const OpsCount = styled.span`
  padding: 1.75px 6.75px;
  border: 0.75px solid rgba(56, 200, 212, 0.2);
  border-radius: 3px;
  background: rgba(56, 200, 212, 0.08);
  font-family: ${fonts.mono};
  font-size: 8px;
  letter-spacing: 0.5px;
  color: ${colors.cyan};
`;

const OpsList = styled.div`
  margin-top: 2px;
`;

const OpsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 0 9.75px;
  ${({ $last }) => !$last && `border-bottom: 0.75px solid ${colors.hairline};`}

  span {
    font-family: ${fonts.body};
    font-size: 11px;
    line-height: 16.5px;
    color: ${colors.textSoft};
  }
`;

const Toggle = styled.div`
  position: relative;
  width: 30px;
  height: 15.996px;
  border-radius: 8px;
  flex-shrink: 0;
  background: ${({ $on }) => ($on ? colors.cyan : '#172038')};
  border: 0.75px solid
    ${({ $on }) => ($on ? 'rgba(56,200,212,0.35)' : colors.hairline)};
`;

const Knob = styled.span`
  position: absolute;
  top: 1.99px;
  ${({ $on }) => ($on ? 'left: 14.5px;' : 'left: 1.99px;')}
  width: 12px;
  height: 12px;
  border-radius: 6px;
  background: ${({ $on }) => ($on ? colors.bg : '#2c3e54')};
`;


const Bars = styled.div`
  margin-top: 10px;
  display: flex;
  align-items: flex-end;
  gap: 5px;
  height: 44px;
`;

const Bar = styled.div`
  flex: 1;
  border-radius: 2px 2px 0 0;
  background: ${({ $active }) =>
    $active ? colors.cyan : 'rgba(56,200,212,0.25)'};
`;
